'use client'

import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

const pulseDotVariants = cva('relative inline-flex flex-shrink-0', {
  variants: {
    size: {
      xs: 'h-1.5 w-1.5',
      sm: 'h-2 w-2',
      md: 'h-2.5 w-2.5',
      lg: 'h-3 w-3',
    },
  },
  defaultVariants: {
    size: 'sm',
  },
})

const colorVarMap = {
  lime: 'var(--color-lime, #84cc16)',
  green: 'var(--color-green)',
  cyan: 'var(--color-cyan)',
  pink: 'var(--color-pink)',
  orange: 'var(--color-orange)',
  purple: 'var(--color-purple)',
} as const

export type PulseDotColor = keyof typeof colorVarMap

export interface PulseDotProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'color'>,
    VariantProps<typeof pulseDotVariants> {
  color?: PulseDotColor
  /** When false, renders a static dot without the ping animation */
  pulse?: boolean
  /** Accessible label for screen readers */
  label?: string
}

const PulseDot = React.forwardRef<HTMLSpanElement, PulseDotProps>(
  ({ className, size, color = 'lime', pulse = true, label, ...props }, ref) => {
    const bg = colorVarMap[color]
    return (
      <span
        ref={ref}
        role={label ? 'status' : undefined}
        aria-label={label}
        className={cn(pulseDotVariants({ size }), className)}
        {...props}
      >
        {pulse && (
          <span
            aria-hidden
            className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75"
            style={{ background: bg }}
          />
        )}
        <span
          aria-hidden
          className="relative inline-flex h-full w-full rounded-full"
          style={{ background: bg }}
        />
      </span>
    )
  }
)
PulseDot.displayName = 'PulseDot'

export { PulseDot, pulseDotVariants }
